import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, WhatsappLogo, PaypalLogo, Truck, ShieldCheck, CreditCard, ShareNetwork, ArrowsClockwise, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { whatsappLink } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { Header } from "@/components/Header";
import { ProductSpecs } from "@/components/ProductSpecs";
import { fbqTrack } from "@/lib/track";
import { trackCartAdd } from "@/lib/visits";
import { Footer } from "@/components/Footer";
import { PRODUCT_SPECS } from "@/data/productSpecs";
import { CardBadges } from "@/components/Products";
import { useProducts } from "@/lib/productsStore";
import { ProductImg, mainSrc, displaySrc } from "@/components/ProductImg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_BASE = "https://rutadeportiva360.com/api/share";
const formatMXN = (n) => `$${n.toLocaleString("es-MX")} MXN`;

const EXTRAS = [
  {
    icon: ShieldCheck,
    title: "Garantía Ruta 360",
    body: "Te atiende una persona, no un bot. Si algo sale mal con tu pedido, lo resolvemos contigo directamente.",
  },
  {
    icon: Truck,
    title: "Envíos a todo México",
    body: "Envío a todo México: $99. Gratis en compras desde $1,399 MXN.",
  },
  {
    icon: CreditCard,
    title: "Formas de pago",
    body: "Mercado Pago, PayPal o contra entrega en Xalapa.",
  },
];

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { products, loaded } = useProducts();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [imgIdx, setImgIdx] = useState(0);
  const [barVisible, setBarVisible] = useState(false);
  const btnRef = useRef(null);
  const touchX = useRef(null);
  const fetchedFor = useRef(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    setImgIdx(0);
    const cached = products.find((p) => p.id === id);
    if (cached) {
      setProduct(cached);
      setStatus("ok");
      if (fetchedFor.current !== id) {
        fetchedFor.current = id;
        axios.get(`${API}/products/${id}`, { timeout: 8000 }).then((res) => setProduct(res.data)).catch(() => {});
      }
      return;
    }
    if (!loaded) {
      setStatus("loading");
      return;
    }
    if (fetchedFor.current !== id) {
      fetchedFor.current = id;
      setStatus("loading");
      const attempt = (n) => {
        axios
          .get(`${API}/products/${id}`, { timeout: 8000 })
          .then((res) => {
            setProduct(res.data);
            setStatus("ok");
          })
          .catch((err) => {
            if (err.response?.status === 404) {
              setStatus("error");
            } else if (n < 2) {
              setTimeout(() => attempt(n + 1), [1000, 2000][n]);
            } else {
              setStatus("neterror");
            }
          });
      };
      attempt(0);
    }
  }, [id, loaded, products, retryTick]);

  useEffect(() => {
    if (!product) return;
    [product.image, ...(product.images || [])]
      .filter(Boolean)
      .forEach((u) => {
        const im = new Image();
        im.src = displaySrc(u);
      });
  }, [product]);

  const gallery = product
    ? [...new Set([product.image, ...(product.images || [])].filter(Boolean))].slice(0, 6)
    : [];

  const [displayedImg, setDisplayedImg] = useState(null);
  const [visorOpen, setVisorOpen] = useState(false);
  const targetImg = gallery[imgIdx];
  const shownImg = displayedImg && gallery.includes(displayedImg) ? displayedImg : targetImg;

  useEffect(() => {
    if (!targetImg) return;
    if (displayedImg && gallery.includes(displayedImg) && displayedImg !== targetImg) {
      const im = new Image();
      im.onload = () => setDisplayedImg(targetImg);
      im.onerror = () => setDisplayedImg(targetImg);
      im.src = displaySrc(targetImg);
      if (im.complete && im.naturalWidth > 0) setDisplayedImg(targetImg);
    } else {
      setDisplayedImg(targetImg);
    }
    [imgIdx - 1, imgIdx + 1].forEach((i) => {
      if (i >= 0 && i < gallery.length) {
        const p = new Image();
        p.src = mainSrc(gallery[i]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetImg]);

  useEffect(() => {
    if (!visorOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setVisorOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visorOpen]);

  useEffect(() => {
    if (product) {
      fbqTrack("ViewContent", { content_name: product.name, content_ids: [product.id], content_type: "product", value: product.price_mxn, currency: "MXN" });
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;
    setRelated(products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4));
  }, [product, products]);

  useEffect(() => {
    if (status !== "ok" || !btnRef.current) return;
    const obs = new IntersectionObserver(
      ([e]) => setBarVisible(!e.isIntersecting && e.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    obs.observe(btnRef.current);
    return () => obs.disconnect();
  }, [status]);

  useEffect(() => {
    document.body.toggleAttribute("data-buybar", barVisible);
    return () => document.body.removeAttribute("data-buybar");
  }, [barVisible]);

  const specsData = product ? PRODUCT_SPECS[product.id] : null;
  const shareUrl = product ? `${SHARE_BASE}/${product.id}` : "";

  const share = async () => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch && navigator.share) {
      try {
        await navigator.share({ title: product.name, text: `${product.name} | Ruta 360`, url: shareUrl });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) {
      setImgIdx((i) => (dx < 0 ? Math.min(i + 1, gallery.length - 1) : Math.max(i - 1, 0)));
    }
    touchX.current = null;
  };

  return (
    <div data-testid="product-detail-page" className="bg-white min-h-screen">
      <Header />
      <main className="pt-[100px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
          <div className="flex items-center justify-between mb-10">
            <button
              data-testid="back-to-catalog-button"
              onClick={() => navigate("/")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-black/50 transition-colors duration-300 hover:text-black"
            >
              <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Volver al catálogo
            </button>
            {status === "ok" && product && (
              <button
                data-testid="share-product-button"
                onClick={share}
                className="inline-flex items-center gap-2 text-sm font-semibold text-black/50 transition-colors duration-300 hover:text-[#FF5508]"
              >
                <ShareNetwork size={17} />
                Compartir
              </button>
            )}
          </div>

          {status === "loading" && (
            <div data-testid="detail-loading" className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 animate-pulse">
              <div className="aspect-square w-full bg-black/5 rounded-2xl" />
              <div>
                <div className="h-4 w-24 bg-black/10 rounded mb-4" />
                <div className="h-12 lg:h-16 w-4/5 bg-black/10 rounded mb-5" />
                <div className="h-8 w-44 bg-black/5 rounded mb-4" />
                <div className="h-4 w-64 bg-black/5 rounded" />
              </div>
            </div>
          )}

          {status === "error" && (
            <div data-testid="detail-not-found">
              <h1 className="font-display text-4xl uppercase font-semibold text-[#111418] mb-4">
                Producto no encontrado
              </h1>
              <p className="text-black/55 text-sm">Es posible que el producto ya no esté disponible.</p>
            </div>
          )}

          {status === "neterror" && (
            <div data-testid="detail-neterror" className="text-center py-10">
              <p className="text-black/50 text-sm mb-4">No pudimos cargar los productos.</p>
              <button
                data-testid="detail-retry-button"
                onClick={() => {
                  fetchedFor.current = null;
                  setStatus("loading");
                  setRetryTick((t) => t + 1);
                }}
                className="bg-black text-white text-xs font-semibold px-6 py-3 rounded-full hover:bg-[#FF5508] transition-colors"
              >
                Reintentar
              </button>
            </div>
          )}

          {status === "ok" && product && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div
                    data-testid="detail-gallery"
                    className="relative border border-black/10 rounded-2xl overflow-hidden bg-white touch-pan-y"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                  >
                    <CardBadges product={product} />
                    <button
                      data-testid="detail-image-zoom"
                      onClick={() => setVisorOpen(true)}
                      className="w-full cursor-zoom-in"
                      aria-label="Ver foto en grande"
                    >
                      <ProductImg
                        data-testid="detail-image"
                        src={shownImg}
                        alt={product.name}
                        className="w-full aspect-square object-contain p-8 select-none pointer-events-none"
                        eager
                        w={900}
                        draggable={false}
                      />
                    </button>
                    {gallery.length > 1 && (
                      <div data-testid="gallery-dots" className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 sm:hidden">
                        {gallery.map((_, i) => (
                          <span
                            key={i}
                            className={`w-2 h-2 rounded-full transition-colors ${i === imgIdx ? "bg-[#FF5508]" : "bg-black/20"}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {gallery.length > 1 && (
                    <div data-testid="gallery-thumbs" className="mt-3 flex gap-3 overflow-x-auto pb-1">
                      {gallery.map((u, i) => (
                        <button
                          key={u}
                          data-testid={`gallery-thumb-${i}`}
                          onClick={() => setImgIdx(i)}
                          aria-label={`Foto ${i + 1}`}
                          className={`w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 bg-white transition-colors ${
                            i === imgIdx ? "border-[#FF5508]" : "border-black/10 hover:border-black/30"
                          }`}
                        >
                          <ProductImg src={u} alt="" w={160} eager className="w-full h-full object-contain p-1.5" />
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <p className="text-sm font-semibold text-[#FF5508] mb-3">{product.category}</p>
                  <h1
                    data-testid="detail-name"
                    className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-semibold leading-none tracking-tight text-[#111418]"
                  >
                    {product.name}
                  </h1>
                  <p data-testid="detail-price" className="font-bold text-3xl text-[#111418] mt-5 flex items-center gap-3">
                    {formatMXN(product.price_mxn)}
                    {product.disponible === false && (
                      <span data-testid="detail-agotado" className="text-xs font-semibold bg-black/15 text-black/60 px-3 py-1.5 rounded-full">
                        Agotado
                      </span>
                    )}
                  </p>
                  <p data-testid="detail-shipping-note" className="mt-3 flex items-center gap-2 text-sm text-black/60">
                    <Truck size={17} weight="fill" className="text-[#FF5508] shrink-0" />
                    {product.price_mxn >= 1399
                      ? "✓ Envío gratis a todo México"
                      : "Envío a todo México: $99 · Gratis en compras desde $1,399"}
                  </p>

                  <div data-testid="delivery-times" className="mt-4 bg-[#F6F7F8] rounded-xl p-4">
                    <p className="flex items-center gap-2 text-sm font-semibold text-[#111418] mb-2">
                      <Truck size={16} weight="fill" className="text-[#FF5508]" /> Tiempo de entrega
                    </p>
                    <ul className="text-sm text-black/60 flex flex-col gap-1">
                      <li>Veracruz y estados vecinos: 1 a 3 días</li>
                      <li>Resto de México: 3 días</li>
                      <li>Ejidos y zonas de difícil acceso: 3 a 7 días</li>
                    </ul>
                  </div>

                  <div ref={btnRef} data-testid="detail-buy-buttons" className="mt-7 flex flex-col gap-3">
                    {product.disponible === false ? (
                      <a
                        data-testid="detail-whatsapp-agotado"
                        href={whatsappLink(`Hola Ruta 360, me interesa ${product.name}. ¿Cuándo vuelve a estar disponible?`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-12 rounded-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-semibold transition-colors duration-300 hover:bg-black"
                      >
                        <WhatsappLogo size={18} weight="fill" />
                        Pregúntanos cuándo llega
                      </a>
                    ) : (
                      <>
                        <button
                          data-testid="detail-mercadopago-button"
                          onClick={() => navigate(`/checkout?producto=${product.id}`)}
                          className="w-full h-12 rounded-full bg-[#009EE3] text-white text-sm font-semibold transition-colors duration-300 hover:bg-black"
                        >
                          Pagar con Mercado Pago
                        </button>
                        <button
                          data-testid="detail-paypal-button"
                          onClick={() => navigate(`/checkout?producto=${product.id}`)}
                          className="w-full h-12 rounded-full bg-[#FFC439] text-[#111418] text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors duration-300 hover:bg-[#f0b429]"
                        >
                          <PaypalLogo size={18} weight="fill" />
                          Pagar con PayPal
                        </button>
                        <button
                          data-testid="detail-add-cart-button"
                          onClick={() => {
                            fbqTrack("AddToCart", { content_name: product.name, content_ids: [product.id], content_type: "product", value: product.price_mxn, currency: "MXN" });
                            trackCartAdd();
                            add(product);
                          }}
                          className="w-full h-12 rounded-full inline-flex items-center justify-center gap-2 bg-black text-white text-sm font-semibold transition-colors duration-300 hover:bg-[#FF5508]"
                        >
                          Agregar al carrito
                        </button>
                      </>
                    )}
                  </div>

                  <p data-testid="detail-payment-methods" className="mt-3 text-[13px] text-black/45 leading-relaxed flex items-start gap-2">
                    <CreditCard size={14} className="shrink-0 mt-0.5" />
                    <span>Con Mercado Pago puedes pagar con tarjeta de débito o crédito, transferencia SPEI o efectivo en OXXO y otras tiendas. Con PayPal, con tarjeta o tu cuenta PayPal.</span>
                  </p>

                  <p data-testid="detail-warranty" className="mt-4 text-[13px] text-black/45 text-center flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                    <Link to="/ayuda#garantia" className="inline-flex items-center gap-1.5 hover:text-black transition-colors no-underline">
                      <ShieldCheck size={14} className="text-black/45" /> 1 año de garantía
                    </Link>
                    <span className="text-black/25">·</span>
                    <Link to="/ayuda#cambios" className="inline-flex items-center gap-1.5 hover:text-black transition-colors no-underline">
                      <ArrowsClockwise size={14} className="text-black/45" /> Cambios en 7 días
                    </Link>
                  </p>

                  <p className="mt-3 text-xs text-black/45">
                    ¿Estás en Xalapa? También puedes pagar contra entrega.
                  </p>

                  <p className="text-black/60 text-base leading-relaxed mt-5 max-w-lg">
                    {product.tagline}
                  </p>

                  {specsData && (
                    <div data-testid="must-know" className="mt-7">
                      <h3 className="font-bold text-base text-[#111418] mb-3">Lo que tienes que saber</h3>
                      <ul className="list-disc pl-5 flex flex-col gap-1.5 text-sm text-black/70">
                        {specsData.specs.slice(0, 4).map(([label, value]) => (
                          <li key={label}>
                            <span className="font-semibold text-black">{label}:</span> {value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              </div>

              <ProductSpecs productId={product.id} />

              <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {EXTRAS.map((e) => (
                  <div
                    key={e.title}
                    data-testid={`detail-extra-${e.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="bg-[#F6F7F8] rounded-2xl p-6"
                  >
                    <e.icon size={22} weight="fill" className="text-[#FF5508] mb-3" />
                    <h3 className="font-bold text-base text-[#111418] mb-1.5">{e.title}</h3>
                    <p className="text-sm text-black/55 leading-relaxed">{e.body}</p>
                  </div>
                ))}
              </div>

              {related.length > 0 && (
                <div className="mt-20">
                  <h2 className="font-display text-3xl sm:text-4xl uppercase font-semibold tracking-tight text-[#111418] mb-8">
                    Más de <span className="text-[#FF5508]">{product.category}</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {related.map((p) => {
                      const pLocal = p.image.startsWith("/images/") || p.image.startsWith("/api/");
                      return (
                        <article
                          key={p.id}
                          data-testid={`related-card-${p.id}`}
                          onClick={() => navigate(`/producto/${p.id}`)}
                          className="group bg-white border border-black/10 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-black/30 hover:shadow-lg hover:-translate-y-1"
                        >
                          <div className="relative h-44 overflow-hidden bg-white">
                            <CardBadges product={p} />
                            <ProductImg
                              src={p.image}
                              alt={p.name}
                              w={400}
                              className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
                                pLocal ? "object-contain p-3" : "object-cover"
                              }`}
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-base text-[#111418] leading-snug transition-colors duration-300 group-hover:text-[#FF5508]">{p.name}</h3>
                            <p className="font-bold text-black mt-2">{formatMXN(p.price_mxn)}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      {barVisible && <div className="h-20 lg:hidden" aria-hidden="true" />}
      <Footer />

      {status === "ok" && product && barVisible && (
        <div
          data-testid="mobile-buy-bar"
          className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.12)] px-4 py-3 flex items-center gap-3"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#111418] truncate">{product.name}</p>
            <p className="text-sm font-bold text-[#111418]">{formatMXN(product.price_mxn)}</p>
          </div>
          {product.disponible === false ? (
            <a
              data-testid="mobile-bar-whatsapp"
              href={whatsappLink(`Hola Ruta 360, me interesa ${product.name}. ¿Cuándo vuelve a estar disponible?`)}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 px-4 rounded-full bg-[#25D366] text-white text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
            >
              <WhatsappLogo size={15} weight="fill" />
              Pregúntanos cuándo llega
            </a>
          ) : (
            <>
          <button
            data-testid="mobile-bar-mercadopago"
            onClick={() => navigate(`/checkout?producto=${product.id}`)}
            className="h-10 px-3 rounded-full bg-[#009EE3] text-white text-xs font-semibold inline-flex items-center shrink-0"
          >
            Mercado Pago
          </button>
          <button
            data-testid="mobile-bar-paypal"
            onClick={() => navigate(`/checkout?producto=${product.id}`)}
            className="h-10 px-3 rounded-full bg-[#FFC439] text-[#111418] text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
          >
            <PaypalLogo size={15} weight="fill" />
            PayPal
          </button>
            </>
          )}
        </div>
      )}

      {visorOpen && (
        <div
          data-testid="detail-visor"
          data-lenis-prevent
          onClick={() => setVisorOpen(false)}
          className="fixed inset-0 z-[1200] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button
            data-testid="detail-visor-close"
            onClick={() => setVisorOpen(false)}
            aria-label="Cerrar"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center shadow-lg z-10"
          >
            <X size={20} weight="bold" />
          </button>
          <img
            data-testid="detail-visor-image"
            src={mainSrc(shownImg)}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />
        </div>
      )}
    </div>
  );
};
